// Frontend <-> Backend interaction library

import axios, { CancelToken, CancelTokenSource } from "axios";
import {EventEmitter} from "events";
import { HTTPGetAPI, HTTPPostAPI } from '@/lib/httpapi';

export class FrontendClient extends EventEmitter {
    userData: HTTPPostAPI["auth/info"]["result"] = {
        isLoggedIn: false,
    };

    baseURL: string;
    baseWSURL: string;

    errorPromptMessage:string = null;

    wsClient: WebsocketClient;

    constructor() {
        super();

        // this.baseURL = `${window.location.protocol}//${window.location.hostname}${window.location.pathname}`;
        this.baseWSURL = `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.hostname}`;
        this.baseURL = `${window.location.protocol}//${window.location.hostname}`;
        let baseURLMatch = this.baseURL.match(/^([\s\S]+?)\/*$/);
        this.baseURL = baseURLMatch ? baseURLMatch[1] : "";

        this.updateUserInfo(true);

        this.wsClient = new WebsocketClient(this);
    }

    get<T extends keyof HTTPGetAPI>(path: T,query?: HTTPGetAPI[T]["query"]): Promise<HTTPGetAPI[T]["result"]> {
        return new Promise((resolve,reject) => {
            axios.get(`${this.baseURL}/${path}`,{
                params: query,
            }).then((res) => {
                if(res.status == 200) {
                    let data;

                    try {
                        if(typeof data === "string") {
                            data = JSON.parse(res.data);
                        }
                        else {
                            data = res.data;
                        }
                    }
                    catch(parseErr) {
                        reject(parseErr);
                    }
                    finally {
                        if(data.success) {
                            resolve(data.data);
                        }
                        else {
                            reject(data.error);
                        }
                    }
                }
                else {
                    reject(`server returned non-200 status code: ${res.status}`);
                }
            }).catch(reject);
        });
    }

    async getAuthenticated<T extends keyof HTTPGetAPI>(path: T,query?: HTTPGetAPI[T]["query"]): Promise<HTTPGetAPI[T]["result"]> {
        await this.asyncLoggedIn();

        return await this.get(path,query);
    }

    post<T extends keyof HTTPPostAPI>(path: T,body?: HTTPPostAPI[T]["query"],progress?: (ProgressEvent) => void): Promise<HTTPPostAPI[T]["result"]> {
        return new Promise((resolve,reject) => {
            axios.post(`${this.baseURL}/${path}`,body,{onUploadProgress: progress}).then((res) => {
                if(res.status == 200) {
                    let data;

                    try {
                        if(typeof data === "string") {
                            data = JSON.parse(res.data);
                        }
                        else {
                            data = res.data;
                        }
                    }
                    catch(parseErr) {
                        reject(parseErr);
                    }
                    finally {
                        if(data.success) {
                            resolve(data.data);
                        }
                        else {
                            reject(data.error);
                        }
                    }
                }
                else {
                    reject(`server returned non-200 status code: ${res.status}`);
                }
            }).catch(reject);
        });
    }

    async postAuthenticated<T extends keyof HTTPPostAPI>(path: T,body?: HTTPPostAPI[T]["query"]): Promise<HTTPPostAPI[T]["result"]> {
        await this.asyncLoggedIn();

        return await this.post(path,body);
    }

    async postEx<T extends keyof HTTPPostAPI>(path: T,body?: HTTPPostAPI[T]["query"]): Promise<HTTPPostAPI[T]["result"]> {
        try {
            return await this.post(path,body);
        }
        catch(e) {
            this.giveError(e.toString());
            throw e;
        }
    }

    async postAuthenticatedEx<T extends keyof HTTPPostAPI>(path: T,body?: HTTPPostAPI[T]["query"]): Promise<HTTPPostAPI[T]["result"]> {
        try {
            return await this.postAuthenticated(path,body);
        }
        catch(e) {
            this.giveError(e.toString());
            throw e;
        }
    }

    postCancellable<T extends keyof HTTPPostAPI>(path: T,body?: HTTPPostAPI[T]["query"]): [CancelTokenSource,Promise<HTTPPostAPI[T]["result"]>] {
        let source = axios.CancelToken.source();
        return [source,new Promise((resolve,reject) => {
            axios.post(`${this.baseURL}/${path}`,body,{
                cancelToken: source.token,
            }).then((res) => {
                if(res.status == 200) {
                    let data;

                    try {
                        if(typeof data === "string") {
                            data = JSON.parse(res.data);
                        }
                        else {
                            data = res.data;
                        }
                    }
                    catch(parseErr) {
                        reject(parseErr);
                    }
                    finally {
                        if(data.success) {
                            resolve(data.data);
                        }
                        else {
                            reject(data.error);
                        }
                    }
                }
                else {
                    reject(`server returned non-200 status code: ${res.status}`);
                }
            }).catch(reject);
        })];
    }

    openFacebookLogin() {
        // make sure to call from within "onclick" or something
        // in order to prevent the popup blocker from being annoying
        let popup = window.open(`${this.baseURL}/auth/facebook`, "Facebook Login", "");

        let timerID = setInterval(() => {
            if(!popup) {
                // failed to open popup, 3: uwu
                console.log("Failed to open login pop-up! Ruh-roh!");
                clearInterval(timerID);
            }
            else {
                if(popup.closed !== false) {
                    clearInterval(timerID);
                    this.updateUserInfo();
                }
            }
        }, 200);
    }

    async updateUserInfo(isFirstStart?: boolean) {
        this.userData = await this.post("auth/info");
        this.emit("userInfoUpdated",this.userData,isFirstStart);
        if(this.userData.isLoggedIn) {
            this.emit("loggedIn",isFirstStart);
        }
    }

    onceLoggedIn(cb: (atPageStart: boolean) => void) {
        if(this.userData.isLoggedIn) {
            return process.nextTick(() => {cb(true);});
        }

        this.once("loggedIn",cb)
    }

    asyncLoggedIn() {
        return new Promise<boolean>((res) => {
            this.onceLoggedIn(res);
        })
    }

    async logOut() {
        await this.post("auth/logout");
        await this.updateUserInfo();
    }

    giveError(message:string){
        this.errorPromptMessage = message;
        this.emit("errorPromptMessage");
    }
    clearError(){
        this.errorPromptMessage = null;
        this.emit("errorPromptMessage")
    }
}

interface WindowExt extends Window {
    frontendClient: FrontendClient;
}
declare var window: WindowExt;

import { WebsocketClient } from './wsclient';

export let frontendClient = new FrontendClient();
window.frontendClient = frontendClient;

